import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getBlogPosts, deleteBlogPost } from '../../services/firebaseService';
import toast from 'react-hot-toast';

export default function AdminBlogs() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchPosts();
  }, []);

  async function fetchPosts() {
    try {
      const data = await getBlogPosts();
      setPosts(data);
    } catch (err) {
      toast.error(t('admin.failedLoad'));
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id, title) {
    if (!window.confirm(t('admin.deleteConfirm', { name: title }))) return;
    try {
      await deleteBlogPost(id);
      setPosts(prev => prev.filter(p => p.id !== id));
      toast.success(t('admin.postDeleted'));
    } catch {
      toast.error(t('admin.failedDelete'));
    }
  }

  const getTitle = (post) => isAr && post.titleAr ? post.titleAr : post.title;
  const getExcerpt = (post) => isAr && post.excerptAr ? post.excerptAr : post.excerpt;

  const filtered = posts.filter(p =>
    getTitle(p)?.toLowerCase().includes(search.toLowerCase()) ||
    p.category?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="admin-loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="admin-blogs">
      <div className="admin-page-header">
        <div>
          <h1>{t('admin.blogPosts')}</h1>
          <p>{posts.length} {t('admin.totalPosts')}</p>
        </div>
        <Link to="/admin/blogs/new" className="btn btn-primary">
          <i className="fas fa-plus"></i> {t('admin.newPost')}
        </Link>
      </div>

      <div className="admin-toolbar">
        <div className="admin-search">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder={t('admin.searchPosts')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="admin-empty">
          <i className="fas fa-file-alt"></i>
          <p>{t('admin.noPostsFound')}</p>
          <Link to="/admin/blogs/new" className="btn btn-primary">{t('admin.createFirstPost')}</Link>
        </div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t('admin.title')}</th>
                <th>{t('admin.category')}</th>
                <th>{t('admin.date')}</th>
                <th>{t('admin.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(post => (
                <tr key={post.id}>
                  <td>
                    <div className="admin-post-cell">
                      {post.image && (
                        <img src={post.image} alt="" className="admin-post-thumb" />
                      )}
                      <div>
                        <strong>{getTitle(post)}</strong>
                        <small>{getExcerpt(post)?.substring(0, 80)}...</small>
                      </div>
                    </div>
                  </td>
                  <td><span className="admin-badge">{post.category || '—'}</span></td>
                  <td>{post.date || '—'}</td>
                  <td>
                    <div className="admin-actions">
                      <Link to={`/admin/blogs/edit/${post.id}`} className="admin-action-btn edit" title={t('admin.editPost')}>
                        <i className="fas fa-edit"></i>
                      </Link>
                      <button onClick={() => handleDelete(post.id, getTitle(post))} className="admin-action-btn delete" title={t('admin.failedDelete')}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
